import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Card,
    Empty,
    Radio,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import {
    InfoCircleOutlined,
    MergeCellsOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface BuildingBlocksAIConfigRow {
    id?: number;
    config_key: string;
    system_prompt: string;
    user_prompt_template: string;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface BuildingBlocksAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        building_blocks_ai_config?: BuildingBlocksAIConfigRow[];
    };
}

interface ElementInfo {
    element: string;
    configKey: string;
    question: string;
}

const COMBINED_KEY = 'combined_all';
const DEFAULT_KEY = 'default';

// Mirrors src/ai-experts/services/building-blocks-ai.service.ts ELEMENT_CONFIG_KEYS + CORE_QUESTIONS.
const ELEMENTS: ElementInfo[] = [
    { element: 'Willingness to Pay a Premium', configKey: 'willingness_to_pay_a_premium', question: 'Could the brand charge more?' },
    { element: 'Brand Attachment', configKey: 'brand_attachment', question: 'Are customers emotionally attached to the brand?' },
    { element: 'Quality Value Tradeoff (PQV)', configKey: 'quality_value_tradeoff', question: 'Does the brand feel worth its price?' },
    { element: 'Alignment Depth', configKey: 'alignment_depth', question: 'Do brand touchpoints deliver a great experience?' },
    { element: 'Behavioural Loyalty', configKey: 'behavioural_loyalty', question: 'Are customers likely to buy again?' },
    { element: 'Attitudinal Loyalty', configKey: 'attitudinal_loyalty', question: 'Are customers proud to choose this brand?' },
    { element: 'Market Awareness', configKey: 'market_awareness', question: 'Is the brand front of mind?' },
    { element: 'Associations', configKey: 'associations', question: 'Does the brand stand out in a positive way?' },
];

const COMBINED_PLACEHOLDERS = (
    <span>
        <code>{'{overall_score}'}</code> — overall brand equity score with classification
        <br />
        <code>{'{all_elements_data}'}</code> — all 8 elements with scores, inputs, and per-element interpretation guidance
    </span>
);

const PER_ELEMENT_PLACEHOLDERS = (
    <span>
        <code>{'{core_question}'}</code> — the element's core question
        <br />
        <code>{'{element_name}'}</code> — the element name
        <br />
        <code>{'{element_score}'}</code> — the element's score (with classification)
        <br />
        <code>{'{overall_score}'}</code> — overall brand equity score
        <br />
        <code>{'{element_inputs}'}</code> — the element's input scores
    </span>
);

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for Building Blocks AI.
 *
 * Vertical sidebar tabs (Combined / Default / one per element). Per-element
 * rows that don't exist in the v1 baseline render the Default template's
 * prompts as greyed placeholders; typing in the editor adds a new row to the
 * draft payload (DELETE+INSERT round-trip lets the deserialiser persist it).
 */
export const BuildingBlocksAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<BuildingBlocksAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.building_blocks_ai_config ?? [];

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    /** Update an existing row, or insert a new row if no row exists for that config_key. */
    const upsertRow = (configKey: string, patch: Partial<BuildingBlocksAIConfigRow>) => {
        editor.setPayload((prev) => {
            const list = prev!.configTableRows.building_blocks_ai_config ?? [];
            const idx = list.findIndex((r) => r.config_key === configKey);
            const next =
                idx >= 0
                    ? list.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [
                          ...list,
                          {
                              config_key: configKey,
                              system_prompt: '',
                              user_prompt_template: '',
                              is_active: 1,
                              ...patch,
                          },
                      ];
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, building_blocks_ai_config: next },
            };
        });
    };

    const findRow = (configKey: string): BuildingBlocksAIConfigRow | undefined =>
        rows.find((r) => r.config_key === configKey);

    const defaultRow = findRow(DEFAULT_KEY);
    const elementsWithRow = ELEMENTS.filter((el) => !!findRow(el.configKey));
    const customCount = elementsWithRow.length;
    const fallbackCount = ELEMENTS.length - customCount;

    const primaryValue: ProviderModelTempValue = {
        provider: (settings.BUILDING_BLOCKS_AI_PRIMARY_PROVIDER as ProviderId) || guessProvider(settings.BUILDING_BLOCKS_AI_PRIMARY_MODEL),
        model: settings.BUILDING_BLOCKS_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.BUILDING_BLOCKS_AI_PRIMARY_TEMPERATURE ?? 0.7),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider: (settings.BUILDING_BLOCKS_AI_FALLBACK_PROVIDER as ProviderId) || guessProvider(settings.BUILDING_BLOCKS_AI_FALLBACK_MODEL),
        model: settings.BUILDING_BLOCKS_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.BUILDING_BLOCKS_AI_FALLBACK_TEMPERATURE ?? 0.7),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            BUILDING_BLOCKS_AI_PRIMARY_PROVIDER: next.provider,
            BUILDING_BLOCKS_AI_PRIMARY_MODEL: next.model,
            BUILDING_BLOCKS_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            BUILDING_BLOCKS_AI_FALLBACK_PROVIDER: next.provider,
            BUILDING_BLOCKS_AI_FALLBACK_MODEL: next.model,
            BUILDING_BLOCKS_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    const mode = settings.BUILDING_BLOCKS_AI_MODE ?? 'combined';

    if (rows.length === 0) {
        return <Empty description="No building_blocks_ai_config rows — has the v1 baseline been seeded?" />;
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Per-element building-block summaries.{' '}
                    <strong>{customCount}</strong> element
                    {customCount === 1 ? '' : 's'} with custom prompts,{' '}
                    <strong>{fallbackCount}</strong> using the default template.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="Mode" size="small" style={{ marginBottom: 16 }}>
                <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                    <code>BUILDING_BLOCKS_AI_MODE</code> — combined makes one LLM call covering
                    all elements (cheaper, but if the combined response is malformed the runtime
                    falls back to individual). Individual makes one call per element.
                </Paragraph>
                <Radio.Group
                    value={mode}
                    onChange={(e) => setSetting('BUILDING_BLOCKS_AI_MODE', e.target.value)}
                    options={[
                        { value: 'combined', label: 'Combined (single call)' },
                        { value: 'individual', label: 'Individual (per-element)' },
                    ]}
                    optionType="button"
                />
            </Card>

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <Card>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 600 }}
                    items={[
                        {
                            key: COMBINED_KEY,
                            label: (
                                <span>
                                    {findRow(COMBINED_KEY) ? (
                                        <Badge dot status="success" offset={[-4, 0]}>
                                            <span>
                                                <MergeCellsOutlined style={{ marginRight: 8 }} />
                                                Combined Mode
                                            </span>
                                        </Badge>
                                    ) : (
                                        <span>
                                            <MergeCellsOutlined style={{ marginRight: 8 }} />
                                            Combined Mode
                                        </span>
                                    )}
                                </span>
                            ),
                            children: (
                                <RowEditor
                                    row={findRow(COMBINED_KEY)}
                                    fallbackRow={undefined}
                                    onChange={(patch) => upsertRow(COMBINED_KEY, patch)}
                                    topAlert={
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            style={{ marginBottom: 16 }}
                                            message="Combined Mode prompt"
                                            description={
                                                <span>
                                                    Used when{' '}
                                                    <code>BUILDING_BLOCKS_AI_MODE</code> is{' '}
                                                    <code>combined</code>. The AI sees all 8
                                                    elements at once and returns a single JSON
                                                    object with one insight per element. Per-element
                                                    interpretation guidance from each per-element
                                                    tab is automatically injected into the data block.
                                                </span>
                                            }
                                        />
                                    }
                                    placeholdersAlert={COMBINED_PLACEHOLDERS}
                                />
                            ),
                        },
                        {
                            key: DEFAULT_KEY,
                            label: (
                                <span>
                                    {findRow(DEFAULT_KEY) ? (
                                        <Badge dot status="success" offset={[-4, 0]}>
                                            <span>
                                                <SettingOutlined style={{ marginRight: 8 }} />
                                                Default Template
                                            </span>
                                        </Badge>
                                    ) : (
                                        <span>
                                            <SettingOutlined style={{ marginRight: 8 }} />
                                            Default Template
                                        </span>
                                    )}
                                </span>
                            ),
                            children: (
                                <RowEditor
                                    row={findRow(DEFAULT_KEY)}
                                    fallbackRow={undefined}
                                    onChange={(patch) => upsertRow(DEFAULT_KEY, patch)}
                                    topAlert={
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            style={{ marginBottom: 16 }}
                                            message="Default per-element template"
                                            description="Fallback prompt used in individual mode for any element whose per-element row is missing or inactive. Also used as the per-element interpretation guidance injected into combined-mode calls."
                                        />
                                    }
                                    placeholdersAlert={PER_ELEMENT_PLACEHOLDERS}
                                />
                            ),
                        },
                        ...ELEMENTS.map((el) => {
                            const row = findRow(el.configKey);
                            const usingDefault = !row;
                            return {
                                key: el.configKey,
                                label: (
                                    <span>
                                        {row ? (
                                            <Badge dot status="success" offset={[-4, 0]}>
                                                <span>{el.element}</span>
                                            </Badge>
                                        ) : (
                                            <span>{el.element}</span>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <RowEditor
                                        row={row}
                                        fallbackRow={defaultRow}
                                        onChange={(patch) => upsertRow(el.configKey, patch)}
                                        topAlert={
                                            <>
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    icon={<InfoCircleOutlined />}
                                                    style={{ marginBottom: 12 }}
                                                    message={
                                                        <span>
                                                            <strong>Core Question:</strong>{' '}
                                                            {el.question}
                                                        </span>
                                                    }
                                                />
                                                {usingDefault && (
                                                    <Alert
                                                        type="info"
                                                        style={{ marginBottom: 16 }}
                                                        message="Using default template"
                                                        description="This element currently uses the default template. Type into the fields below to create a custom configuration."
                                                    />
                                                )}
                                            </>
                                        }
                                        placeholdersAlert={PER_ELEMENT_PLACEHOLDERS}
                                    />
                                ),
                            };
                        }),
                    ]}
                />
            </Card>
        </div>
    );
};

interface RowEditorProps {
    row: BuildingBlocksAIConfigRow | undefined;
    /** Default template, shown as greyed placeholder when `row` is undefined. */
    fallbackRow: BuildingBlocksAIConfigRow | undefined;
    onChange: (patch: Partial<BuildingBlocksAIConfigRow>) => void;
    topAlert: React.ReactNode;
    placeholdersAlert: React.ReactNode;
}

const RowEditor: React.FC<RowEditorProps> = ({
    row,
    fallbackRow,
    onChange,
    topAlert,
    placeholdersAlert,
}) => {
    return (
        <div>
            {topAlert}

            {row && (
                <Space style={{ marginBottom: 12 }} wrap>
                    <Tag color={isActiveBool(row.is_active) ? 'green' : 'default'}>
                        {isActiveBool(row.is_active) ? 'active' : 'inactive'}
                    </Tag>
                    <Switch
                        checked={isActiveBool(row.is_active)}
                        onChange={(checked) => onChange({ is_active: checked ? 1 : 0 })}
                        checkedChildren="on"
                        unCheckedChildren="off"
                    />
                </Space>
            )}

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="building_blocks_ai"
                    label="System Prompt"
                    value={row?.system_prompt ?? ''}
                    onChange={(v) => onChange({ system_prompt: v })}
                    rows={8}
                    placeholder={fallbackRow?.system_prompt ?? ''}
                />
            </div>
            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="building_blocks_ai"
                    label="User Prompt Template"
                    value={row?.user_prompt_template ?? ''}
                    onChange={(v) => onChange({ user_prompt_template: v })}
                    rows={14}
                    placeholder={fallbackRow?.user_prompt_template ?? ''}
                />
            </div>

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Available Placeholders"
                description={placeholdersAlert}
            />
        </div>
    );
};
